import type { Game } from "../engine/game";
import type { UserSettings } from "./userSettings";
import type { SoundManager } from "./sound/soundManager";
import { playUiSound, setUiSoundVolume, UiSound } from "./sound/uiSoundPlayer";
import type { MusicPlayer } from "./sound/musicPlayer";
import type { Camera } from "./camera";
import type { SpriteRenderer } from "./spriteRenderer";
import type { AnimationLoader } from "./animationLoader";
import type { DrawableManager } from "./drawableManager";
import type { HudRenderer } from "./hud";
import type { InventoryPanelRenderer } from "./inventoryPanel";
import type { HudMessageRenderer } from "./hudMessageRenderer";
import { showPauseMenu } from "./overlays";
import { showSettingsScreen } from "./settingsScreen";
import { TILESET_PATHS } from "./mainMenu";
import * as TileImageLoader from "./tileImageLoader";
import { playerSpriteComposer } from "./playerSpriteComposer";
import { clearSeenHints } from "./hintStorage";

export interface PauseDeps {
	game: Game;
	userSettings: UserSettings;
	soundManager: SoundManager;
	musicPlayer: MusicPlayer;
	camera: Camera;
	spriteRenderer: SpriteRenderer;
	animationLoader: AnimationLoader;
	drawableManager: DrawableManager;
	hudRenderer: HudRenderer;
	inventoryPanel: InventoryPanelRenderer;
	hudMessageRenderer: HudMessageRenderer;
	getCurrentAssetsDir: () => string;
	setCurrentAssetsDir: (dir: string) => void;
	onTileImagesRefreshed: (dashedSquareImg: HTMLCanvasElement, aimRedImg: HTMLCanvasElement) => void;
	onLayoutChange: () => Promise<void>;
	onQuit: () => void;
}

export class PauseController {
	private readonly deps: PauseDeps;
	private paused: boolean = false;
	private pauseOverlay: HTMLElement | null = null;

	constructor(deps: PauseDeps) {
		this.deps = deps;
	}

	isPaused(): boolean {
		return this.paused;
	}

	toggle(): void {
		if (this.paused && this.pauseOverlay) {
			playUiSound(UiSound.CLICK);
			this.removePauseOverlay();
		} else if (!this.paused) {
			playUiSound(UiSound.CLICK);
			this.pauseOverlay = this.createPauseMenu();
			this.paused = true;
		}
	}

	/** Safe to call multiple times. */
	dismiss(): void {
		this.removePauseOverlay();
	}

	private removePauseOverlay(): void {
		if (this.pauseOverlay) {
			document.body.removeChild(this.pauseOverlay);
			this.pauseOverlay = null;
		}
		this.paused = false;
	}

	private createPauseMenu(): HTMLElement {
		return showPauseMenu({
			seed: this.deps.game.seed,
			stats: this.deps.game.stats,
			onResume: () => {
				this.removePauseOverlay();
			},
			onSettings: () => {
				showSettingsScreen(
					this.deps.userSettings,
					async () => {
						await this.applySettingsChange();
					},
					() => {
						clearSeenHints();
						this.deps.game.resetSeenHints();
					},
				);
			},
			onQuit: () => {
				this.removePauseOverlay();
				this.deps.onQuit();
			},
		});
	}

	private async applySettingsChange(): Promise<void> {
		await this.applyTilesetChange();
		this.deps.soundManager.setVolumeLevel(this.deps.userSettings.getEffectiveSoundVolume());
		setUiSoundVolume(this.deps.userSettings.getEffectiveSoundVolume());
		this.deps.musicPlayer.setVolume(this.deps.userSettings.getEffectiveMusicVolume());
		this.deps.hudRenderer.refreshLabels();
		this.deps.inventoryPanel.refreshLabels();
		// Follow-mode changes the canvas width, so re-run the layout, not just re-center.
		this.deps.spriteRenderer.setFollowTarget(
			this.deps.userSettings.cameraFollowPlayer ? this.deps.game.player! : null,
		);
		await this.deps.onLayoutChange();
		// Rebuild pause menu so labels reflect new language.
		if (this.pauseOverlay) {
			document.body.removeChild(this.pauseOverlay);
		}
		this.pauseOverlay = this.createPauseMenu();
	}

	/**
	 * Step sequence is load-bearing: setAssetsDir before preloadAll, clear
	 * drawables before syncWithBoard reads new images, dashed/aim refetch sees
	 * the freshly-primed cache.
	 */
	private async applyTilesetChange(): Promise<void> {
		const newPaths = TILESET_PATHS[this.deps.userSettings.tileset] ?? TILESET_PATHS.hd;
		if (newPaths.assetsDir !== this.deps.getCurrentAssetsDir()) {
			this.deps.setCurrentAssetsDir(newPaths.assetsDir);
			TileImageLoader.setAssetsDir(newPaths.assetsDir);
			TileImageLoader.clearCache();
			playerSpriteComposer.clearCache();
			TileImageLoader.preloadAll();
			const board = this.deps.game.getCurrentBoard();
			this.deps.drawableManager.clear();
			await this.deps.drawableManager.syncWithBoard(board);
			await this.deps.spriteRenderer.loadFloorTile();
			await this.deps.spriteRenderer.syncFloor(board);
			const dashedSquareImg = await TileImageLoader.getImage(
				"DashedSquare.png",
				this.deps.camera.cellWidth,
				this.deps.camera.cellHeight,
			);
			const aimRedImg = await TileImageLoader.getImage(
				"AimRed.png",
				this.deps.camera.cellWidth,
				this.deps.camera.cellHeight,
			);
			this.deps.onTileImagesRefreshed(dashedSquareImg, aimRedImg);
		}
	}
}
